import type { Edge, Node } from "@xyflow/react";
import {
    type BuildFacetGraphParams,
    type ExplodeFlow,
    type ExplodeNodeData,
    NodeKind,
    NodeRole,
} from "../types";

const NODE_WIDTH = 200;
const COLUMN_GAP = 72;
const ROW_GAP = 132;
const ROOT_Y = 0;
const GRID_TOP = 130;
const MAX_COLUMNS = 8;
const TARGET_ASPECT = 3.6;
const SUB_MAX_COLUMNS = 8;
const SUB_COLUMN_GAP = 48;
const SUB_ROW_GAP = 92;
const CHILD_GAP = 150;
const OVERLAY_Z = 20;

const columnsForAspect = (count: number): number => {
    const cellAspect = ROW_GAP / (NODE_WIDTH + COLUMN_GAP);
    const ideal = Math.round(Math.sqrt(count * TARGET_ASPECT * cellAspect));
    return Math.min(Math.max(ideal, 1), MAX_COLUMNS);
};

const node = (
    id: string,
    x: number,
    y: number,
    data: ExplodeNodeData,
    zIndex = 0,
): Node<ExplodeNodeData> => ({ id, type: NodeKind.BUCKET, position: { x, y }, data, zIndex });

export const buildFacetGraph = ({
    categories,
    categoryField,
    rootLabel,
    expandedValue,
    subItems = [],
    subField,
}: BuildFacetGraphParams): ExplodeFlow => {
    const columns = columnsForAspect(categories.length || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const gridWidth = Math.min(categories.length || 1, columns) * stepX - COLUMN_GAP;
    const gridCenterX = (gridWidth - NODE_WIDTH) / 2;
    const expandedIndex = expandedValue
        ? categories.findIndex((category) => category.value === expandedValue)
        : -1;
    const isExpanding = expandedIndex >= 0 && subItems.length > 0;

    const categoryNodes = categories.map((category, index) =>
        node(
            `c-${index}`,
            (index % columns) * stepX,
            GRID_TOP + Math.floor(index / columns) * ROW_GAP,
            {
                label: category.value,
                count: category.count,
                role: NodeRole.GROUP,
                selection: [{ field: categoryField, value: category.value }],
                dimmed: isExpanding && index !== expandedIndex,
                focused: isExpanding && index === expandedIndex,
            },
        ),
    );

    const rootNode = node("root", gridCenterX, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
        dimmed: isExpanding,
    });

    const categoryEdges = categories.map<Edge>((_, index) => ({
        id: `e-root-c${index}`,
        source: "root",
        target: `c-${index}`,
        type: "default",
        style: { stroke: isExpanding ? "#f4f0fc" : "#c0b6e8", strokeWidth: 1 },
    }));

    if (!isExpanding) {
        return { nodes: [rootNode, ...categoryNodes], edges: categoryEdges };
    }

    const blockX = (expandedIndex % columns) * stepX;
    const blockY = GRID_TOP + Math.floor(expandedIndex / columns) * ROW_GAP;

    const subColumns = Math.min(SUB_MAX_COLUMNS, Math.max(subItems.length, 1));
    const subStepX = NODE_WIDTH + SUB_COLUMN_GAP;
    const subGridWidth = Math.min(subItems.length, subColumns) * subStepX - SUB_COLUMN_GAP;
    const childStartX = blockX + NODE_WIDTH / 2 - subGridWidth / 2;
    const childTop = blockY + CHILD_GAP;

    const childNodes = subItems.map((sub, index) =>
        node(
            `s-${index}`,
            childStartX + (index % subColumns) * subStepX,
            childTop + Math.floor(index / subColumns) * SUB_ROW_GAP,
            {
                label: sub.value,
                count: sub.count,
                role: NodeRole.GROUP,
                selection: [
                    { field: categoryField, value: expandedValue ?? "" },
                    { field: subField ?? "", value: sub.value },
                ],
                dimmed: false,
            },
            OVERLAY_Z,
        ),
    );

    const childEdges = subItems.map<Edge>((_, index) => ({
        id: `e-sub-${index}`,
        source: `c-${expandedIndex}`,
        target: `s-${index}`,
        type: "default",
        zIndex: OVERLAY_Z - 1,
        style: { stroke: "#9775fa", strokeWidth: 1.4 },
    }));

    return {
        nodes: [rootNode, ...categoryNodes, ...childNodes],
        edges: [...categoryEdges, ...childEdges],
    };
};
